import type { ChangeEvent, FocusEvent, HTMLAttributes, KeyboardEvent, ReactElement } from 'react'
import { omit } from 'lodash-es'
import { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { QuantityContext } from './CartContext'
import type { QuantityFieldError } from '../../../utils/orderUnits'
import { hasOrderUnitPiece, validateQuantity } from '../../../utils/orderUnits'
import { useTranslate } from '../../../utils/translate'

type QuantityFieldErrorsProps = {
  errors: QuantityFieldError[]
  orderUnitInfo: Core.OrderUnitInfo
} & TranslateProps

function QuantityFieldErrors({
  errors,
  orderUnitInfo: { orderUnitShort, minOrder, intervalOrder },
  t,
}: Readonly<QuantityFieldErrorsProps>) {
  const locale = useSelector<State, string>((state) => state.getIn(['shop', 'locale']))

  const decimalPlaces = intervalOrder.toString().split('.')[1]?.length ?? 0

  const formatter = new Intl.NumberFormat(locale.replace('_', '-'), {
    minimumFractionDigits: decimalPlaces,
  })

  const examples = [...Array(3).keys()]
    .map((factor) => `${formatter.format(factor * intervalOrder + minOrder)} ${orderUnitShort}`)
    .join(', ')

  if (errors.includes('minimumQuantityNotReached')) {
    return (
      <span className="product-order-unit-quantity-field-errors">
        <p>
          {t('productOrderUnitQuantity.errors.minimumQuantityNotReached', {
            minimum: formatter.format(minOrder),
            unit: orderUnitShort,
          })}
        </p>
      </span>
    )
  }

  if (errors.includes('invalidQuantityIncrement')) {
    return (
      <span className="product-order-unit-quantity-field-errors">
        <p>
          {t('productOrderUnitQuantity.errors.invalidQuantityIncrement', {
            step: formatter.format(intervalOrder),
            unit: orderUnitShort,
            examples,
          })}
        </p>
      </span>
    )
  }

  return null
}

type QuantityFieldProps = {
  className?: string
  lineItem: Core.ProductLineItem
  min?: number
  quantity?: number | string
  invalidProduct?: boolean
} & HTMLAttributes<HTMLElement>

export default function QuantityField(props: Readonly<QuantityFieldProps>): ReactElement {
  const { className = '', min: min_ = 1, lineItem, quantity = 1, invalidProduct = false } = props
  const quantityScope: any = useContext(QuantityContext)

  const t = useTranslate('shop', 'components.productComponent')
  const isCartBusy = useSelector<State, boolean>((state) => state.getIn(['view', 'cart', 'isBusy'], false))

  const furtherHtmlAttributes = omit(props, ['className', 'lineItem', 'min', 'quantity', 'invalidProduct', 't'])

  const min = lineItem.orderUnitInfo.minOrder ?? min_
  const delta = lineItem.orderUnitInfo.intervalOrder ?? 1

  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState(quantity)

  useEffect(() => {
    setValue(quantity)
  }, [quantity])

  const errors = validateQuantity(Number(value), lineItem.orderUnitInfo.minOrder, lineItem.orderUnitInfo.intervalOrder)

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleBlur(event as unknown as FocusEvent<HTMLInputElement>)
  }

  const setQuantity = (quantity: number) => {
    const prevQuantity = quantityScope.getQuantity()

    const normalizedQuantity = Math.max(quantity, min)

    if (prevQuantity !== normalizedQuantity && errors.length === 0) {
      quantityScope
        .setQuantity(normalizedQuantity, normalizedQuantity - prevQuantity)
        // Catch it so we do not get an unhandled rejection error.

        .catch(() => {})
    }
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (event.target.value.length === 0) setValue(min)

    if (!isCartBusy) {
      const quantity = parseFloat(event.target.value)
      setQuantity(quantity)
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.replace(',', '.')

    if (inputValue === '' || /^(\d+)(\.\d{0,10})?$/.test(inputValue)) setValue(inputValue)
  }

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.target.setSelectionRange(0, event.target.value.length)
  }

  const changeQuantity = (delta: number) => {
    const quantity = (quantityScope.getQuantity() * 1000 + delta * 1000) / 1000
    setQuantity(quantity)
    // Waiting for all event handlers to finish, then blur the input field.
    // This solves an issue of undesired focus states on touch devices (UNITY-5984).
    setTimeout(() => inputRef.current?.blur())
  }

  return (
    <div className={className}>
      <button
        onClick={() => changeQuantity(-delta)}
        disabled={isCartBusy || invalidProduct}
        aria-label={
          lineItem.orderUnitInfo.intervalOrder !== 1
            ? t('productOrderUnitQuantity.decreaseButton.accessibilityLabel', {
                value: lineItem.orderUnitInfo.intervalOrder,
                unit: lineItem.orderUnitInfo.orderUnitShort,
              })
            : t('productQuantity.decreaseButton.accessibilityLabel')
        }
      />
      <div>
        <input
          ref={inputRef}
          id="quantity"
          inputMode="numeric"
          pattern="[0-9]*"
          min={min}
          value={value.toString()}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          autoComplete="off"
          disabled={isCartBusy || invalidProduct}
          data-testid="quantityInputField"
          {...furtherHtmlAttributes}
        />
        <QuantityFieldErrors orderUnitInfo={lineItem.orderUnitInfo} t={t} errors={errors} />
      </div>
      <button
        onClick={() => changeQuantity(delta)}
        disabled={isCartBusy || invalidProduct}
        aria-label={
          lineItem.orderUnitInfo.intervalOrder !== 1
            ? t('productOrderUnitQuantity.increaseButton.accessibilityLabel', {
                value: lineItem.orderUnitInfo.intervalOrder,
                unit: lineItem.orderUnitInfo.orderUnitShort,
              })
            : t('productQuantity.increaseButton.accessibilityLabel')
        }
      />
      {!hasOrderUnitPiece(lineItem.orderUnitInfo.orderUnitShort) && (
        <span className="product-quantity-field-order-unit">{lineItem.orderUnitInfo.orderUnitShort}</span>
      )}
    </div>
  )
}
